/**
 * GoalFormDialog - Dialog para criar/editar metas de vendedores
 * 
 * Funcionalidades:
 * - Seleção de vendedor (SellerSelector)
 * - Tipo de meta (receita ou quantidade de negócios)
 * - Período (data início e fim)
 * - Valor alvo
 * - Observações
 * 
 * @version 1.0.0
 * @author Sistema
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { WorkspaceMultiSelect } from '@/components/crm/WorkspaceMultiSelect';
import { useSellerGoals, SellerGoal, CreateGoalInput, UpdateGoalInput } from '@/hooks/useSellerGoals';

// ============================================================================
// SCHEMA - Validação do formulário
// ============================================================================

const goalFormSchema = z.object({
  seller_id: z.string().min(1, 'Selecione um vendedor'),
  goal_type: z.enum(['revenue', 'deals_count'], {
    required_error: 'Selecione o tipo de meta',
  }),
  target_value: z.coerce
    .number({ invalid_type_error: 'Informe um valor válido' })
    .positive('O valor deve ser maior que zero'),
  period_start: z.date({ required_error: 'Selecione a data inicial' }),
  period_end: z.date({ required_error: 'Selecione a data final' }),
  notes: z.string().optional().nullable(),
  /** IDs dos workspaces associados */
  workspace_ids: z.array(z.string()).default([]),
}).refine((data) => data.period_end >= data.period_start, {
  message: 'A data final deve ser maior ou igual à data inicial',
  path: ['period_end'],
});

type GoalFormValues = z.infer<typeof goalFormSchema>;

// ============================================================================
// PROPS - Tipos do componente
// ============================================================================

interface GoalFormDialogProps {
  /** Controla se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onOpenChange: (open: boolean) => void;
  /** Meta para edição (null = criar nova) */
  goal?: SellerGoal | null;
  /** Callback após sucesso */
  onSuccess?: () => void;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function GoalFormDialog({
  open,
  onOpenChange,
  goal,
  onSuccess,
}: GoalFormDialogProps) {
  const { createGoal, updateGoal } = useSellerGoals();
  const isEditing = !!goal;

  // Configuração do formulário
  const form = useForm<GoalFormValues>({
    resolver: zodResolver(goalFormSchema),
    defaultValues: {
      seller_id: '',
      goal_type: 'revenue',
      target_value: 0,
      period_start: undefined,
      period_end: undefined,
      notes: '',
      workspace_ids: [],
    },
  });

  // Reset do formulário quando abre/fecha ou muda a meta
  useEffect(() => {
    if (open) {
      if (goal) {
        // Modo edição: preenche com dados da meta
        form.reset({
          seller_id: goal.seller_id,
          goal_type: goal.goal_type,
          target_value: goal.target_value,
          period_start: new Date(goal.period_start + 'T00:00:00'),
          period_end: new Date(goal.period_end + 'T00:00:00'),
          notes: goal.notes || '',
          workspace_ids: goal.workspace_ids || [],
        });
      } else {
        // Modo criação: limpa formulário
        form.reset({
          seller_id: '',
          goal_type: 'revenue',
          target_value: 0,
          period_start: undefined,
          period_end: undefined,
          notes: '',
          workspace_ids: [],
        });
      }
    }
  }, [open, goal, form]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const onSubmit = async (values: GoalFormValues) => {
    try {
      if (isEditing && goal) {
        // Atualizar meta existente
        const updateInput: UpdateGoalInput = {
          id: goal.id,
          target_value: values.target_value,
          period_start: format(values.period_start, 'yyyy-MM-dd'),
          period_end: format(values.period_end, 'yyyy-MM-dd'),
          notes: values.notes || null,
          workspace_ids: values.workspace_ids,
        };
        await updateGoal.mutateAsync(updateInput);
      } else {
        // Criar nova meta
        const createInput: CreateGoalInput = {
          seller_id: values.seller_id,
          goal_type: values.goal_type,
          target_value: values.target_value,
          period_start: format(values.period_start, 'yyyy-MM-dd'),
          period_end: format(values.period_end, 'yyyy-MM-dd'),
          notes: values.notes || null,
          workspace_ids: values.workspace_ids,
        };
        await createGoal.mutateAsync(createInput);
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // Erro já tratado no hook com toast
    }
  };

  const isSubmitting = createGoal.isPending || updateGoal.isPending;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Meta' : 'Nova Meta'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Atualize os dados da meta do vendedor.'
              : 'Defina uma meta para um vendedor da equipe.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Vendedor - desabilitado em modo edição */}
            <FormField
              control={form.control}
              name="seller_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendedor *</FormLabel>
                  <FormControl>
                    <SellerSelector
                      value={field.value}
                      onSelect={field.onChange}
                      disabled={isEditing}
                      placeholder="Selecione o vendedor"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tipo de Meta - desabilitado em modo edição */}
            <FormField
              control={form.control}
              name="goal_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Meta *</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isEditing}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="revenue">Faturamento (R$)</SelectItem>
                      <SelectItem value="deals_count">Quantidade de Negócios</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Valor Alvo */}
            <FormField
              control={form.control}
              name="target_value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Valor da Meta *
                    <span className="text-muted-foreground text-xs ml-1">
                      ({form.watch('goal_type') === 'revenue' ? 'R$' : 'negócios'})
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      step={form.watch('goal_type') === 'revenue' ? '0.01' : '1'}
                      placeholder={form.watch('goal_type') === 'revenue' ? '50000.00' : '10'}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Período - Data Início e Fim */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="period_start"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data Início *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'dd/MM/yyyy', { locale: ptBR })
                            ) : (
                              <span>Selecione</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={ptBR}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="period_end"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data Fim *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'dd/MM/yyyy', { locale: ptBR })
                            ) : (
                              <span>Selecione</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const startDate = form.getValues('period_start');
                            return startDate ? date < startDate : false;
                          }}
                          locale={ptBR}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Observações */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Informações adicionais sobre esta meta..."
                      className="resize-none"
                      rows={3}
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Workspaces - Seleção múltipla */}
            <FormField
              control={form.control}
              name="workspace_ids"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workspaces</FormLabel>
                  <FormControl>
                    <WorkspaceMultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Selecione os workspaces (opcional)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Botões */}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar Meta'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
